import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input, Label } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Gauge, ShieldAlert, TrendingUp, FileCheck2, ClipboardList, BookOpen, Layers, Info,
  Plus, RotateCw, Lock, ArrowRight, CircleAlert, AlertTriangle,
} from "lucide-react";

const STATE_BADGE = {
  measured: { v: "success", l: "Measured" }, calculated: { v: "success", l: "Calculated" },
  estimate: { v: "warning", l: "Estimate" }, declaration: { v: "warning", l: "Declaration" },
  blank: { v: "outline", l: "No data" },
};
const STATUS_BADGE = { draft: "outline", in_review: "warning", published: "success" };

const Sparkline = ({ values, color = "#00E65B", label }) => {
  const vals = values.filter((v) => v != null);
  if (vals.length < 2) return null;
  const w = 120, h = 32, pad = 3;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (w - 2 * pad);
    const y = h - pad - ((v - min) / span) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = vals[vals.length - 1];
  return (
    <div className="flex items-center gap-2" data-testid={`si-spark-${label}`}>
      <div className="min-w-[64px]"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-mono text-sm font-bold">{last}</div></div>
      <svg width={w} height={h} className="overflow-visible">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} strokeLinejoin="round" strokeLinecap="round" />
        {vals.map((v, i) => {
          const x = pad + (i / (vals.length - 1)) * (w - 2 * pad);
          const y = h - pad - ((v - min) / span) * (h - 2 * pad);
          return <circle key={i} cx={x} cy={y} r={i === vals.length - 1 ? 2.5 : 1.5} fill={color} />;
        })}
      </svg>
    </div>
  );
};

const Range = ({ agg }) => agg?.score == null ? <span className="text-muted-foreground text-sm">Not available</span> : (
  <span className="font-mono">
    <span className="text-2xl font-bold">{agg.score}</span>
    <span className="text-xs text-muted-foreground ml-2">{agg.coverage}% covered · range {agg.range[0]}–{agg.range[1]}</span>
  </span>
);

export default function ScoreIntelligence() {
  const [meta, setMeta] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ entity: "", period: "FY2024", vertical: "landscaping", mode: "sme" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = async () => { const { data } = await api.get("/score-intel/assessments"); setAssessments(data.assessments); };
  useEffect(() => {
    api.get("/score-intel/meta").then(({ data }) => setMeta(data)).catch(() => {});
    loadList();
  }, []);

  const openAssessment = async (id) => {
    const { data } = await api.get(`/score-intel/assessments/${id}`);
    setSel(data);
  };
  const runPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/score-intel/activation/preview", { period: form.period, vertical: form.vertical }); setPreview(data); }
    catch (e) { toast.error("Preview failed"); } finally { setBusy(false); }
  };
  const create = async () => {
    if (!form.entity) return toast.error("Name the entity to assess");
    setBusy(true);
    try {
      const { data } = await api.post("/score-intel/assessments", form);
      toast.success("Assessment created (Draft)"); await loadList(); openAssessment(data.id); setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="score-intelligence-page">
      <div>
        <div className="label-eyebrow mb-1 flex items-center gap-2"><Gauge className="h-3.5 w-3.5 text-accent" /> Add-on · Control Tower</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Score Intelligence</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Five separate decision outputs from the data you already hold — performance, resilience, business value,
          evidence confidence and stakeholder readiness. No single master score; coverage-normalised and fully traceable.
        </p>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Left rail — activation + assessment list */}
        <div className="space-y-4">
          <div className="card-flat p-4 space-y-3" data-testid="si-activation">
            <div className="label-eyebrow">Activate an assessment</div>
            <div><Label>Entity</Label><Input value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} placeholder="Legal entity name" data-testid="si-entity" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Period</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="si-period" /></div>
              <div><Label>Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="si-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.modes || []).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Vertical</Label>
              <Select value={form.vertical} onValueChange={(v) => setForm({ ...form, vertical: v })}>
                <SelectTrigger data-testid="si-vertical"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.verticals || []).map((v) => <SelectItem key={v.vertical} value={v.vertical}>{v.vertical_label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={runPreview} disabled={busy} data-testid="si-preview-btn">Preview</Button>
              <Button variant="primary" size="sm" className="flex-1" onClick={create} disabled={busy} data-testid="si-create-btn"><Plus className="h-3.5 w-3.5" /> Assess</Button>
            </div>
            {preview && (
              <div className="border-t border-border pt-3 text-xs space-y-2" data-testid="si-preview-result">
                <div>Expected coverage <span className="font-mono font-bold">{preview.expected_coverage}%</span> · {preview.indicators_with_evidence}/{preview.indicators_total} indicators have evidence.</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(preview.score_types_available).map(([k, v]) => (
                    <Badge key={k} variant={v ? "success" : "outline"} className="text-[9px]">{k.replace(/_/g, " ")}</Badge>
                  ))}
                </div>
                {preview.missing_data.length > 0 && (
                  <div className="text-muted-foreground">Missing: {preview.missing_data.map((m) => m.label).join(", ")}</div>
                )}
                <div className={preview.gate_internal.eligible ? "text-emerald-400" : "text-[#FFB800]"}>
                  {preview.gate_internal.eligible ? "Meets internal release gate." : "Below internal release gate — informational only until more data is captured."}
                </div>
              </div>
            )}
          </div>

          <div className="card-flat p-4" data-testid="si-assessment-list">
            <div className="label-eyebrow mb-2">Assessments</div>
            {assessments.length === 0 && <div className="text-xs text-muted-foreground">None yet — activate one above.</div>}
            <div className="space-y-1.5">
              {assessments.map((a) => (
                <button key={a.id} onClick={() => openAssessment(a.id)} data-testid={`si-open-${a.id}`}
                  className={`w-full text-left p-2.5 rounded-sm border text-sm transition-colors ${sel?.id === a.id ? "border-accent bg-accent/10" : "border-border hover:bg-surface-hover"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{a.entity}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {a.authority?.superseded && <Badge variant="outline" className="text-[8px] uppercase opacity-70" data-testid={`si-superseded-${a.id}`}>superseded</Badge>}
                      {!a.authority?.superseded && a.authority?.authoritative && <Badge variant="success" className="text-[8px] uppercase" data-testid={`si-authoritative-${a.id}`}>authoritative</Badge>}
                      {!a.authority?.superseded && a.authority?.warning && a.status !== "published" && <span title={a.authority.warning} data-testid={`si-coexist-${a.id}`}><AlertTriangle className="h-3.5 w-3.5 text-[#FFB800]" /></span>}
                      <Badge variant={STATUS_BADGE[a.status]} className="text-[9px] uppercase">{a.status.replace("_", " ")}</Badge>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{a.period} · {a.vertical}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right — workspace */}
        <div>
          {!sel ? (
            <div className="card-flat p-12 text-center text-muted-foreground" data-testid="si-empty">
              <Gauge className="h-10 w-10 mx-auto mb-3 opacity-40" />
              Activate and run an assessment to open the Score Intelligence workspace.
            </div>
          ) : <Workspace sel={sel} reload={() => openAssessment(sel.id)} meta={meta} />}
        </div>
      </div>
    </div>
  );
}

function Workspace({ sel, reload, meta }) {
  const r = sel.results;
  const [history, setHistory] = useState(null);
  const [consent, setConsent] = useState({ recipient: "", recipient_type: "lender", signed_off_by: "" });
  useEffect(() => {
    api.get(`/score-intel/history?vertical=${sel.vertical}`).then(({ data }) => setHistory(data)).catch(() => setHistory(null));
  }, [sel.vertical, sel.status, sel.id]);
  const setStatus = async (status, scope = "internal") => {
    try { await api.post(`/score-intel/assessments/${sel.id}/status`, { status, scope }); toast.success(`Moved to ${status.replace("_", " ")}`); reload(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "release_gate_blocked") toast.error(`Publication blocked: ${d.reasons.join("; ")}`);
      else toast.error("Transition failed");
    }
  };
  const rerun = async () => { await api.post(`/score-intel/assessments/${sel.id}/rerun`); toast.success("Re-scored"); reload(); };

  return (
    <div className="space-y-4" data-testid="si-workspace">
      {sel.authority?.warning && (
        <div className={`card-flat p-3 flex items-start gap-2 text-[12px] ${sel.authority.superseded ? "border-border text-muted-foreground" : "border-[#FFB800]/40 text-[#FFB800]"}`} data-testid="si-authority-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{sel.authority.warning}</span>
        </div>
      )}
      <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-lg font-bold">{sel.entity} · {sel.period}</div>
          <div className="text-xs text-muted-foreground">Methodology {sel.methodology_version} · {sel.mode === "sme" ? "Guided SME" : "Group / listed"} mode
            {sel.immutable && <span className="text-emerald-400"> · immutable published v{sel.published_version}</span>}</div>
        </div>
        <div className="flex items-center gap-2">
          {sel.authority?.authoritative && <Badge variant="success" className="uppercase text-[10px]" data-testid="si-authoritative">authoritative</Badge>}
          {sel.authority?.superseded && <Badge variant="outline" className="uppercase text-[10px] opacity-70" data-testid="si-superseded">superseded</Badge>}
          <Badge variant={STATUS_BADGE[sel.status]} className="uppercase text-[10px]" data-testid="si-status">{sel.status.replace("_", " ")}</Badge>
          {sel.status !== "published" && <Button variant="outline" size="sm" onClick={rerun} data-testid="si-rerun-btn"><RotateCw className="h-3.5 w-3.5" /> Re-score</Button>}
          {sel.status === "draft" && <Button variant="outline" size="sm" onClick={() => setStatus("in_review")} data-testid="si-review-btn">Send to review</Button>}
          {sel.status === "in_review" && <Button variant="primary" size="sm" onClick={() => setStatus("published")} data-testid="si-publish-btn"><Lock className="h-3.5 w-3.5" /> Publish</Button>}
        </div>
      </div>

      {r.safeguards.length > 0 && (
        <div className="card-flat p-3 border-[#FFB800]/40 bg-[#FFB800]/5 space-y-1" data-testid="si-safeguards">
          {r.safeguards.map((s) => (
            <div key={s.key} className="text-xs flex items-center gap-2">
              {s.severity === "serious" ? <CircleAlert className="h-3.5 w-3.5 text-red-400" /> : <AlertTriangle className="h-3.5 w-3.5 text-[#FFB800]" />}
              {s.message}
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="si-tab-overview"><Gauge className="h-3.5 w-3.5 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="performance" data-testid="si-tab-performance"><TrendingUp className="h-3.5 w-3.5 mr-1" /> Performance</TabsTrigger>
          <TabsTrigger value="benchmarks" data-testid="si-tab-benchmarks"><Layers className="h-3.5 w-3.5 mr-1" /> Benchmarks</TabsTrigger>
          <TabsTrigger value="resilience" data-testid="si-tab-resilience"><ShieldAlert className="h-3.5 w-3.5 mr-1" /> Resilience</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="si-tab-evidence"><FileCheck2 className="h-3.5 w-3.5 mr-1" /> Evidence</TabsTrigger>
          <TabsTrigger value="actions" data-testid="si-tab-actions"><ClipboardList className="h-3.5 w-3.5 mr-1" /> Targets & Actions</TabsTrigger>
          <TabsTrigger value="packs" data-testid="si-tab-packs"><FileCheck2 className="h-3.5 w-3.5 mr-1" /> Stakeholder Packs</TabsTrigger>
          <TabsTrigger value="method" data-testid="si-tab-method"><BookOpen className="h-3.5 w-3.5 mr-1" /> Methodology</TabsTrigger>
        </TabsList>

        {/* Overview — five separate outputs, deliberately not combined */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="si-overview">
            <OutBox title="Sustainability performance"><Range agg={r.performance.overall} /></OutBox>
            <OutBox title="Comparable Core Index"><Range agg={r.performance.comparable_core_index} /></OutBox>
            <OutBox title="Resilience & risk">
              {r.resilience.available ? <span className="font-mono text-2xl font-bold">{r.resilience.score ?? "—"}</span> : <span className="text-sm text-muted-foreground">Not available</span>}
              {r.resilience.available && <div className="text-xs text-muted-foreground mt-1">{r.resilience.risks_tracked} risks · {r.resilience.high_severity} high</div>}
              {r.resilience.available && sel.status === "published" && <div className="text-[10px] text-muted-foreground mt-1" data-testid="si-resilience-asof-overview">as-of {sel.period} snapshot</div>}
            </OutBox>
            <OutBox title="Business value">
              <span className="font-mono text-2xl font-bold">S${(r.business_value.annual_savings_sgd || 0).toLocaleString()}</span>
              <div className="text-xs text-muted-foreground mt-1">savings/yr · {r.business_value.verified_outcomes} verified</div>
            </OutBox>
            <OutBox title="Evidence confidence">
              <span className="font-mono text-2xl font-bold">{r.evidence.confidence}%</span>
              <div className="text-xs text-muted-foreground mt-1">{r.evidence.measured} measured · {r.evidence.estimated} estimated · {r.evidence.blank} blank</div>
            </OutBox>
            <OutBox title="Stakeholder readiness">
              <span className="font-mono text-2xl font-bold">{r.readiness.ready}/{r.readiness.total}</span>
              <div className="text-xs text-muted-foreground mt-1">requirements ready</div>
            </OutBox>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5"><Info className="h-3 w-3" /> These outputs are shown side-by-side and are never averaged into a single traffic-light. Evidence confidence sits beside performance and is never multiplied into it.</p>
        </TabsContent>

        {/* Performance — E/S/G + per-indicator explain */}
        <TabsContent value="performance" className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(r.performance.pillars).map(([k, p]) => (
              <OutBox key={k} title={p.label}><Range agg={p} /></OutBox>
            ))}
          </div>
          <div className="card-flat p-4 space-y-2" data-testid="si-contributions">
            <div className="label-eyebrow mb-1">Indicator contributions — every number is traceable</div>
            {r.performance.contributions.map((c) => (
              <details key={c.key} className="border border-border rounded-sm" data-testid={`si-contrib-${c.key}`}>
                <summary className="flex items-center justify-between gap-2 p-2.5 cursor-pointer text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-[9px]">{c.pillar}</Badge>
                    <span className="truncate">{c.label}{c.core && <span className="text-accent"> ·core</span>}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <Badge variant={STATE_BADGE[c.state].v} className="text-[9px]">{STATE_BADGE[c.state].l}</Badge>
                    <span className="font-mono font-bold w-12 text-right">{c.score ?? "—"}</span>
                  </span>
                </summary>
                <div className="px-3 pb-3 text-xs text-muted-foreground" data-testid={`si-explain-${c.key}`}>{c.explain}</div>
              </details>
            ))}
          </div>
        </TabsContent>

        {/* Benchmarks — own-history trend + metric-level reference (peer percentiles deferred) */}
        <TabsContent value="benchmarks" className="mt-4 space-y-4">
          <div className="card-flat p-4" data-testid="si-own-history">
            <div className="label-eyebrow mb-2">Own-history — period over period (published assessments)</div>
            {history?.available ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4 pb-2 border-b border-border" data-testid="si-sparklines">
                  <Sparkline label="Overall" values={history.points.map((p) => p.overall)} color="#00E65B" />
                  <Sparkline label="CCI" values={history.points.map((p) => p.cci)} color="#D4A84C" />
                  <Sparkline label="Evidence" values={history.points.map((p) => p.evidence_confidence)} color="#4C9FD4" />
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left py-1">Period</th><th className="text-right">Overall</th><th className="text-right">Cov</th>
                    <th className="text-right">E</th><th className="text-right">S</th><th className="text-right">G</th>
                    <th className="text-right">CCI</th><th className="text-right">Evidence</th><th className="text-right">Δ Overall</th>
                  </tr></thead>
                  <tbody>
                    {history.points.map((p, i) => {
                      const prev = history.points[i - 1];
                      const delta = prev && p.overall != null && prev.overall != null ? +(p.overall - prev.overall).toFixed(1) : null;
                      return (
                        <tr key={p.period} className="border-t border-border font-mono" data-testid={`si-hist-${p.period}`}>
                          <td className="text-left py-1.5 font-sans">{p.period}</td>
                          <td className="text-right font-bold">{p.overall}</td>
                          <td className="text-right text-muted-foreground">{p.coverage}%</td>
                          <td className="text-right">{p.E}</td><td className="text-right">{p.S}</td><td className="text-right">{p.G}</td>
                          <td className="text-right">{p.cci}</td><td className="text-right text-muted-foreground">{p.evidence_confidence}%</td>
                          <td className={`text-right ${delta == null ? "text-muted-foreground" : delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>{delta == null ? "—" : (delta >= 0 ? "+" : "") + delta}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">Publish at least two assessments for this vertical to unlock a period-over-period trend.</div>
            )}
          </div>
          <div className="card-flat p-4" data-testid="si-benchmarks">
            <p className="text-xs text-muted-foreground mb-3">Metric-level reference bands only. Cross-tenant peer percentiles are suppressed until a cohort is large enough (≥30 independent groups).</p>
            <div className="space-y-1.5">
              {r.performance.contributions.filter((c) => c.raw != null).map((c) => (
                <div key={c.key} className="flex items-center justify-between text-sm border-b border-border py-1.5" data-testid={`si-bench-${c.key}`}>
                  <span>{c.label}</span>
                  <span className="font-mono text-xs">value {c.raw}{c.unit} · ref p50 {c.benchmark.p50} / p90 {c.benchmark.p90}</span>
                </div>
              ))}
            </div>
            <Badge variant="outline" className="text-[10px] mt-3">Peer percentile · suppressed (cohort &lt; 30)</Badge>
          </div>
        </TabsContent>

        {/* Resilience */}
        <TabsContent value="resilience" className="mt-4">
          <div className="card-flat p-5 space-y-3" data-testid="si-resilience">
            {sel.status === "published" && (
              <div className="text-[11px] text-muted-foreground rounded-sm border border-border px-2.5 py-1.5" data-testid="si-resilience-asof">
                Figures below are an <strong>as-of {sel.period} published snapshot</strong> (v{sel.published_version}) — frozen at publication and not expected to match the live Risk Register, which continues to change.
              </div>
            )}
            {r.resilience.available ? (
              <div className="grid sm:grid-cols-3 gap-3">
                <OutBox title="Resilience score"><span className="font-mono text-2xl font-bold">{r.resilience.score}</span></OutBox>
                <OutBox title="Risks tracked"><span className="font-mono text-2xl font-bold">{r.resilience.risks_tracked}</span><div className="text-xs text-muted-foreground mt-1">{r.resilience.high_severity} high severity</div></OutBox>
                <OutBox title="Conditional S$ exposure"><span className="font-mono text-2xl font-bold">S${(r.resilience.conditional_exposure_sgd || 0).toLocaleString()}</span></OutBox>
              </div>
            ) : <div className="text-sm text-muted-foreground">No risk register data for this entity — resilience output unavailable.</div>}
            <p className="text-[11px] text-muted-foreground">{r.resilience.note}</p>
          </div>
        </TabsContent>

        {/* Evidence confidence + qualification */}
        <TabsContent value="evidence" className="mt-4">
          <div className="card-flat p-4" data-testid="si-evidence">
            <div className="flex items-center gap-4 mb-3">
              <div><div className="label-eyebrow">Confidence</div><div className="font-mono text-3xl font-bold">{r.evidence.confidence}%</div></div>
              <div className="text-xs text-muted-foreground">Origin qualification per contributing value — measured / calculated carry more weight than estimate / declaration; blanks are gaps.</div>
            </div>
            <div className="space-y-1">
              {r.evidence.breakdown.map((b) => (
                <div key={b.key} className="flex items-center justify-between text-sm border-b border-border py-1.5">
                  <span>{b.label}</span><Badge variant={STATE_BADGE[b.state].v} className="text-[9px]">{STATE_BADGE[b.state].l}</Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Targets & Actions — improvement loop into Initiatives */}
        <TabsContent value="actions" className="mt-4">
          <ActionsTab sel={sel} />
        </TabsContent>

        {/* Stakeholder Packs — requirement readiness; external sharing deferred */}
        <TabsContent value="packs" className="mt-4">
          <div className="card-flat p-4 space-y-2" data-testid="si-packs">
            <p className="text-xs text-muted-foreground mb-1">Requirement-by-requirement evidence status. v1 produces <strong>internal</strong> packs only — consent-based external (lender/buyer) sharing is deferred pending legal & methodology validation.</p>
            {r.readiness.requirements.map((req) => (
              <div key={req.key} className="flex items-center justify-between gap-2 border border-border rounded-sm p-2.5" data-testid={`si-req-${req.key}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{req.label}</div>
                  {req.missing.length > 0 && <div className="text-[11px] text-muted-foreground truncate">Missing: {req.missing.join(", ")}</div>}
                </div>
                <Badge variant={req.status === "ready" ? "success" : req.status === "partial" ? "warning" : "outline"} className="text-[9px] uppercase shrink-0">{req.coverage}% · {req.status.replace("_", " ")}</Badge>
              </div>
            ))}
            <Badge variant="outline" className="text-[10px] mt-1"><Lock className="h-3 w-3 mr-1" /> External sharing · consent-gated</Badge>

            {/* Lender/Buyer pack — consent- and gate-respecting */}
            <div className="border-t border-border mt-3 pt-3 space-y-3" data-testid="si-external">
              <div className="label-eyebrow">External (lender / buyer) pack</div>
              {(() => {
                const ext = sel.gate?.external;
                const hasConsent = !!sel.consent;
                const published = sel.status === "published";
                const packReady = published && ext?.eligible && hasConsent;
                const missing = [];
                if (!published) missing.push("assessment not published");
                if (!ext?.eligible) missing.push("external release gate not met");
                if (!hasConsent) missing.push("recipient consent not recorded");
                return (
                  <>
                    <div data-testid="si-pack-verdict" className={`text-xs font-medium rounded-sm px-2.5 py-2 border ${packReady ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5" : "text-[#FFB800] border-[#FFB800]/40 bg-[#FFB800]/5"}`}>
                      {packReady
                        ? "✓ Ready to share under consent — not a public rating"
                        : `Blocked — cannot be shared externally until: ${missing.join("; ")}`}
                    </div>
                    <div className="text-xs space-y-1">
                      <div className={published ? "text-emerald-400" : "text-muted-foreground"}>{published ? "✓ Assessment published" : "• Must be published first"}</div>
                      <div className={ext?.eligible ? "text-emerald-400" : "text-[#FFB800]"}>
                        {ext?.eligible ? "✓ Meets external release gate (≥90% / ≥80% + safeguard review)" : `• External gate not met${ext?.reasons?.length ? ": " + ext.reasons.join("; ") : ""}`}
                      </div>
                      <div className={hasConsent ? "text-emerald-400" : "text-muted-foreground"}>
                        {hasConsent ? `✓ Consent on file — ${sel.consent.recipient} (${sel.consent.recipient_type}), signed off by ${sel.consent.signed_off_by}` : "• No recipient consent recorded"}
                      </div>
                    </div>

                    {!hasConsent && published && ext?.eligible && (
                      <div className="grid sm:grid-cols-3 gap-2" data-testid="si-consent-form">
                        <Input placeholder="Recipient (e.g. DBS Bank)" value={consent.recipient} onChange={(e) => setConsent({ ...consent, recipient: e.target.value })} data-testid="si-consent-recipient" />
                        <Select value={consent.recipient_type} onValueChange={(v) => setConsent({ ...consent, recipient_type: v })}>
                          <SelectTrigger data-testid="si-consent-type"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="lender">Lender</SelectItem><SelectItem value="buyer">Buyer</SelectItem></SelectContent>
                        </Select>
                        <Input placeholder="Signed off by" value={consent.signed_off_by} onChange={(e) => setConsent({ ...consent, signed_off_by: e.target.value })} data-testid="si-consent-signoff" />
                        <Button variant="outline" size="sm" className="sm:col-span-3" data-testid="si-consent-submit"
                          onClick={async () => {
                            if (!consent.recipient || !consent.signed_off_by) return toast.error("Recipient and sign-off are required");
                            try { await api.post(`/score-intel/assessments/${sel.id}/consent`, consent); toast.success("Consent + safeguard sign-off recorded"); reload(); }
                            catch { toast.error("Could not record consent"); }
                          }}>Record consent & safeguard sign-off</Button>
                      </div>
                    )}

                    <Button variant="primary" size="sm" disabled={!(published && ext?.eligible && hasConsent)} data-testid="si-external-pack-btn"
                      onClick={async () => {
                        try {
                          const res = await api.get(`/score-intel/assessments/${sel.id}/external-pack.pdf`, { responseType: "blob" });
                          const u = URL.createObjectURL(res.data); const a = document.createElement("a");
                          a.href = u; a.download = `score_intelligence_external_pack_${sel.id}.pdf`; a.click(); URL.revokeObjectURL(u);
                          toast.success("External pack generated (shared under consent — no public rating claim)");
                          reload();
                        } catch { toast.error("Pack blocked — gate or consent not satisfied"); }
                      }}>
                      <FileCheck2 className="h-3.5 w-3.5" /> Generate external pack (PDF)
                    </Button>
                    <p className="text-[10px] text-muted-foreground">Packs are marked "shared under consent — not a public rating". This never bypasses the v1 safeguards or makes a public positive claim.</p>

                    {(sel.share_ledger || []).length > 0 && (
                      <div className="mt-2" data-testid="si-share-ledger">
                        <div className="label-eyebrow mb-1">Consent ledger — external shares</div>
                        <div className="space-y-1">
                          {sel.share_ledger.map((ev, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 text-[11px] border border-border rounded-sm px-2 py-1.5" data-testid={`si-ledger-${i}`}>
                              <span className="truncate">{ev.recipient || "—"} <span className="text-muted-foreground">({ev.recipient_type})</span> · sign-off {ev.signed_off_by || "—"}</span>
                              <span className="text-muted-foreground font-mono shrink-0">{ev.generated_at ? new Date(ev.generated_at).toLocaleString() : ""} · {ev.generated_by}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </TabsContent>

        {/* Methodology registry (versioned) */}
        <TabsContent value="method" className="mt-4">
          <MethodTab vertical={sel.vertical} meta={meta} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const OutBox = ({ title, children }) => (
  <div className="card-flat p-4" data-testid={`si-out-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="label-eyebrow mb-2">{title}</div>
    <div>{children}</div>
  </div>
);

function ActionsTab({ sel }) {
  const weak = sel.results.performance.contributions.filter((c) => !c.covered || (c.score != null && c.score < 50));
  const raise = async (c) => {
    try { await api.post(`/score-intel/assessments/${sel.id}/actions`, { indicator_key: c.key, title: `Improve ${c.label}` }); toast.success("Initiative proposed — review it in the Investment Register"); }
    catch { toast.error("Could not raise action"); }
  };
  return (
    <div className="card-flat p-4 space-y-2" data-testid="si-actions">
      <p className="text-xs text-muted-foreground mb-1">Raise improvement actions from weak or missing indicators. Actions land as <strong>proposed</strong> initiatives — a human approves, and only verified outcomes update achieved performance.</p>
      {weak.length === 0 && <div className="text-sm text-muted-foreground">No weak indicators — nothing to raise.</div>}
      {weak.map((c) => (
        <div key={c.key} className="flex items-center justify-between gap-2 border border-border rounded-sm p-2.5" data-testid={`si-weak-${c.key}`}>
          <div><div className="text-sm font-medium">{c.label}</div><div className="text-[11px] text-muted-foreground">{c.covered ? `Score ${c.score}` : "No data — data gap (raisable)"}</div></div>
          <Button variant="outline" size="sm" onClick={() => raise(c)} data-testid={`si-raise-${c.key}`}>Raise action <ArrowRight className="h-3 w-3" /></Button>
        </div>
      ))}
      <Link to="/initiatives" className="text-xs text-accent hover:underline inline-flex items-center gap-1 mt-1">Open Investment Register <ArrowRight className="h-3 w-3" /></Link>
    </div>
  );
}

function MaterialityMatrix({ items, vertical }) {
  const W = 320, H = 260, pad = 34;
  const px = (v) => pad + (v / 100) * (W - 2 * pad);
  const py = (v) => H - pad - (v / 100) * (H - 2 * pad);
  const [impactCut, setImpactCut] = useState(50);
  const [financialCut, setFinancialCut] = useState(50);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    api.get(`/score-intel/materiality-threshold/${vertical}`)
      .then(({ data }) => { setImpactCut(data.impact); setFinancialCut(data.financial); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [vertical]);

  const isMaterial = (it) => it.impact >= impactCut && it.financial >= financialCut;
  const material = items.filter(isMaterial);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/score-intel/materiality-threshold/${vertical}`, { impact: Number(impactCut), financial: Number(financialCut) });
      toast.success("Materiality thresholds saved");
    } catch { toast.error("Could not save thresholds"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t border-border pt-3" data-testid="si-materiality-matrix">
      <div className="label-eyebrow mb-1">Double materiality — impact × financial{vertical === "manufacturing" ? " (hazardous-materials focus)" : ""}</div>
      <p className="text-[11px] text-muted-foreground mb-2">Impact materiality (effect on people &amp; environment) plotted against financial materiality (effect on enterprise value). Set the cut-off lines below — issues that cross <strong>both</strong> thresholds are auto-flagged material.</p>
      <div className="flex flex-wrap gap-4 items-start">
        <svg width={W} height={H} className="shrink-0">
          {/* material quadrant shading (beyond both thresholds) */}
          <rect x={px(impactCut)} y={py(100)} width={Math.max(0, px(100) - px(impactCut))} height={Math.max(0, py(financialCut) - py(100))} fill="#00E65B" opacity="0.08" />
          <line x1={pad} y1={py(0)} x2={W - pad} y2={py(0)} stroke="#556" strokeWidth="1" />
          <line x1={px(0)} y1={pad} x2={px(0)} y2={H - pad} stroke="#556" strokeWidth="1" />
          {/* threshold lines */}
          <line x1={px(impactCut)} y1={pad} x2={px(impactCut)} y2={H - pad} stroke="#00E65B" strokeDasharray="4 3" strokeWidth="1.2" data-testid="si-mat-impact-line" />
          <line x1={pad} y1={py(financialCut)} x2={W - pad} y2={py(financialCut)} stroke="#00E65B" strokeDasharray="4 3" strokeWidth="1.2" data-testid="si-mat-financial-line" />
          <text x={W / 2} y={H - 6} fontSize="9" fill="#889" textAnchor="middle">Impact materiality →</text>
          <text x={12} y={H / 2} fontSize="9" fill="#889" textAnchor="middle" transform={`rotate(-90 12 ${H / 2})`}>Financial materiality →</text>
          {items.map((it, i) => {
            const mat = isMaterial(it);
            return (
              <g key={it.key}>
                <circle cx={px(it.impact)} cy={py(it.financial)} r={it.hazardous ? 7 : 5}
                  fill={it.hazardous ? "#FF6B4A" : "#00E65B"} opacity={mat ? "0.95" : "0.4"}
                  stroke={mat ? "#fff" : "none"} strokeWidth={mat ? 1.5 : 0}
                  data-testid={`si-mat-${it.key}`} />
                <text x={px(it.impact) + 9} y={py(it.financial) + 3} fontSize="8" fill="#cdd">{i + 1}</text>
              </g>
            );
          })}
        </svg>
        <div className="space-y-1 text-xs min-w-[200px]">
          {items.map((it, i) => (
            <div key={it.key} className="flex items-center gap-2" data-testid={`si-mat-row-${it.key}`}>
              <span className="font-mono text-[10px] w-4 text-muted-foreground">{i + 1}</span>
              <span className={`h-2 w-2 rounded-full ${it.hazardous ? "bg-[#FF6B4A]" : "bg-[#00E65B]"}`} style={{ opacity: isMaterial(it) ? 1 : 0.4 }} />
              <span className="flex-1" style={{ opacity: isMaterial(it) ? 1 : 0.55 }}>{it.label}</span>
              {isMaterial(it) && <Badge variant="success" className="text-[8px] uppercase" data-testid={`si-mat-badge-${it.key}`}>Material</Badge>}
              <span className="font-mono text-[10px] text-muted-foreground">I{it.impact}/F{it.financial}</span>
            </div>
          ))}
          {items.some((i) => i.hazardous) && <div className="text-[10px] text-[#FF6B4A] pt-1">● Hazardous-materials issue</div>}
        </div>
      </div>

      {/* Threshold controls */}
      <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid="si-mat-thresholds">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground w-28">Impact cut-off</span>
            <input type="range" min="0" max="100" value={impactCut} disabled={!loaded}
              onChange={(e) => setImpactCut(Number(e.target.value))}
              className="w-32 accent-[#00E65B]" data-testid="si-mat-impact-slider" />
            <Input type="number" min="0" max="100" value={impactCut}
              onChange={(e) => setImpactCut(Math.max(0, Math.min(100, Number(e.target.value))))}
              className="w-16 h-7 text-xs" data-testid="si-mat-impact-input" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground w-28">Financial cut-off</span>
            <input type="range" min="0" max="100" value={financialCut} disabled={!loaded}
              onChange={(e) => setFinancialCut(Number(e.target.value))}
              className="w-32 accent-[#00E65B]" data-testid="si-mat-financial-slider" />
            <Input type="number" min="0" max="100" value={financialCut}
              onChange={(e) => setFinancialCut(Math.max(0, Math.min(100, Number(e.target.value))))}
              className="w-16 h-7 text-xs" data-testid="si-mat-financial-input" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] text-muted-foreground" data-testid="si-mat-material-count">
            <strong className="text-foreground">{material.length}</strong> of {items.length} issue{items.length === 1 ? "" : "s"} auto-flagged material
            {material.length > 0 && <>: {material.map((m) => m.label).join(", ")}</>}
          </span>
          <Button variant="outline" size="sm" onClick={save} disabled={saving || !loaded} data-testid="si-mat-save-btn">
            {saving ? "Saving…" : "Save thresholds"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MethodTab({ vertical, meta }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/score-intel/methodology/${vertical}`).then(({ data }) => setM(data)).catch(() => {}); }, [vertical]);
  if (!m) return <div className="text-muted-foreground text-sm p-4">Loading methodology…</div>;
  return (
    <div className="card-flat p-4 space-y-3" data-testid="si-method">
      <div className="flex items-center justify-between">
        <div><div className="label-eyebrow">Methodology registry</div><div className="text-sm">{m.vertical_label} · {m.country} · <span className="font-mono">v{m.version}</span></div></div>
        <Badge variant="success" className="text-[9px] uppercase">{m.status}</Badge>
      </div>
      <div className="text-xs text-muted-foreground">Release policy — internal ≥{m.release_policy.internal.overall * 100}% coverage &amp; ≥{m.release_policy.internal.per_issue * 100}% per issue; external ≥{m.release_policy.external.overall * 100}% &amp; ≥{m.release_policy.external.per_issue * 100}% + safeguard review. No public positive claims in this release.</div>
      {m.materiality && <MaterialityMatrix items={m.materiality} vertical={m.vertical} />}
      <div className="space-y-1">
        {m.indicators.map((i) => (
          <div key={i.key} className="flex items-center justify-between text-sm border-b border-border py-1.5">
            <span className="flex items-center gap-2"><Badge variant="outline" className="text-[9px]">{i.pillar}</Badge>{i.label}{i.core && <span className="text-accent text-[10px]">core</span>}</span>
            <span className="font-mono text-xs text-muted-foreground">w {i.weight} · {i.direction.replace("_", " ")} · ref {i.benchmark.p50}/{i.benchmark.p90}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
